import * as Sentry from '@sentry/react';

import { IMPERSONATION_FLAG } from '../../api/axios.instance';
import { buildInfo } from '../../build-info';

import type { AnyRouter } from '@tanstack/react-router';

const SENTRY_DSN =
  'https://2b998c2669804aae59bd70676ee8634c@o4511315255164928.ingest.us.sentry.io/4511315264077824';

/** Production only (TP's call): staging and development send nothing to Sentry. */
const SENTRY_ENVIRONMENTS = ['production'];

/** Share of page loads / navigations sent as traces. */
const TRACES_SAMPLE_RATE = 0.3;
/**
 * Every production session is recorded (TP's call, 2026-10-26). This needs a Sentry plan with
 * enough reserved replays; on the free plan the allowance is gone on day one. Lower this number
 * to sample; flagged users (`sessionRecordingEnabled`) are recorded regardless of it.
 */
const REPLAYS_SESSION_SAMPLE_RATE = 1.0;
/** Sessions where an error happened: the buffered last minute is sent and recording continues. */
const REPLAYS_ON_ERROR_SAMPLE_RATE = 1.0;

export type SentryAccount = {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  sessionRecordingEnabled: boolean;
};

export const isSentryEnabled = (): boolean =>
  SENTRY_ENVIRONMENTS.includes(import.meta.env.VITE_ENV ?? '');

const IMPERSONATION_ENTRY_PATH = '/impersonate';

/**
 * Impersonated sessions are never replayed: the operator is a superadmin acting in someone
 * else's account. The flag lives in sessionStorage, but the impersonation tab opens on
 * `/impersonate`, whose bootstrap sets the flag only after the app (and Sentry) started, so the
 * entry path counts as impersonation too.
 */
export const isImpersonationSession = (): boolean =>
  sessionStorage.getItem(IMPERSONATION_FLAG) === '1' ||
  window.location.pathname === IMPERSONATION_ENTRY_PATH;

export const initSentry = (router: AnyRouter): void => {
  if (!isSentryEnabled()) {
    return;
  }

  const impersonation = isImpersonationSession();

  Sentry.init({
    dsn: SENTRY_DSN,
    release: buildInfo.release,
    environment: import.meta.env.VITE_ENV,
    sendDefaultPii: true,
    integrations: [
      Sentry.tanstackRouterBrowserTracingIntegration(router),
      // Text and media stay masked (SDK defaults); only app chrome opts out with
      // `data-sentry-unmask` so clicks are readable without exposing customer data.
      ...(impersonation ? [] : [Sentry.replayIntegration({ unmask: ['[data-sentry-unmask]'] })]),
    ],
    tracesSampleRate: TRACES_SAMPLE_RATE,
    replaysSessionSampleRate: impersonation ? 0 : REPLAYS_SESSION_SAMPLE_RATE,
    replaysOnErrorSampleRate: impersonation ? 0 : REPLAYS_ON_ERROR_SAMPLE_RATE,
  });

  Sentry.setTag('app.version', buildInfo.version);
  Sentry.setTag('build.commit', buildInfo.commit);
  Sentry.setTag('build.env', import.meta.env.VITE_ENV);
  Sentry.setTag('impersonation', impersonation ? 'true' : 'false');
};

/**
 * Attaches the signed-in account to every event and replay. When a superadmin flagged the
 * account for recording, forces a full session replay regardless of the sample rate.
 */
export const identifySentryUser = (account: SentryAccount): void => {
  if (!isSentryEnabled()) {
    return;
  }

  Sentry.setUser({
    id: account.id,
    email: account.email,
    username: `${account.firstName} ${account.lastName}`,
  });

  if (!account.sessionRecordingEnabled || isImpersonationSession()) {
    return;
  }

  const replay = Sentry.getReplay();
  if (!replay) {
    return;
  }

  // This runs on every navigation (`beforeLoad`), and `start()` always opens a new replay, so
  // only act when the session is not recorded yet. In buffer mode (error sampling) `flush()`
  // sends the buffered seconds and continues in session mode; otherwise start fresh.
  const mode = replay.getRecordingMode();
  if (mode === 'session') {
    return;
  }

  const started =
    mode === 'buffer'
      ? replay.flush()
      : new Promise<void>((resolve) => {
          replay.start();
          resolve();
        });
  started.catch(() => {
    // A failed start must not surface as an unhandled rejection that Sentry then reports.
  });
};

export const clearSentryUser = (): void => {
  if (!isSentryEnabled()) {
    return;
  }

  Sentry.setUser(null);
};

/** Product action marker on the replay timeline and in error context, e.g. `email.sent`. */
export const trackAction = (name: string, data?: Record<string, unknown>): void => {
  if (!isSentryEnabled()) {
    return;
  }

  Sentry.addBreadcrumb({ category: 'action', message: name, level: 'info', data });
};
